import { useEffect, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { Link, useSearchParams } from "react-router-dom";
import { api, money, ApiError, type PlaysWhere } from "../lib/api";
import { useMeContext } from "../lib/MeContext";
import { Card, Skeleton, PlanBadge, UsageMeter, Notice } from "../components/ui";
import { PersonaPicker } from "../settings/PersonaPicker";
import { useWallet, BucksAmount } from "../lib/WalletContext";
import { Barkeep } from "../rooms/Barkeep";
import { refreshUnlocks } from "../lib/useUnlocks";
import { UNLOCKS, UNLOCK_IDS, type UnlockId } from "@config/plans";

const PLAYS_LABEL: Record<PlaysWhere, string> = {
  home: "Home games",
  live_casino: "Live casino",
  online_cash: "Online cash",
  online_mtt: "Online tournaments",
};

/**
 * The ledger, in words.
 *
 * "Refill" rather than "stake" because that is what it is to the person reading it,
 * and because the word the DATABASE uses is the flag every leaderboard filters on —
 * the two do not have to be the same word and it is clearer that they are not.
 */
const LEDGER_WORDS: Record<string, string> = {
  grant: "Welcome",
  buyin: "Took a seat",
  settle: "Left the table",
  stake: "The Barkeep's refill",
  admin_adjust: "Correction",
};

function when(ts: number | null): string {
  return ts
    ? new Date(ts * 1000).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
    : "—";
}

export function Account() {
  const { getToken } = useAuth();
  const { me, loading, error, refresh } = useMeContext();
  const [params] = useSearchParams();
  const unlockParam = params.get("unlock");
  const unlockBack: UnlockId | null =
    unlockParam && (UNLOCK_IDS as string[]).includes(unlockParam) ? unlockParam as UnlockId : null;
  useEffect(() => { if (unlockBack) refreshUnlocks(); }, [unlockBack]);

  const [busy, setBusy] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);
  const [consent, setConsent] = useState<boolean | null>(null);
  const [consentError, setConsentError] = useState<string | null>(null);
  const wallet = useWallet();
  const [barkeep, setBarkeep] = useState(false);

  if (loading && !me) return <Card><Skeleton lines={5} /></Card>;
  if (error) {
    return (
      <Card>
        <h1>Account</h1>
        <Notice kind="err">
          {error instanceof ApiError ? error.humane : "Couldn't load your account."}
        </Notice>
        <button className="btn-quiet" onClick={() => void refresh()}>Try again</button>
      </Card>
    );
  }
  if (!me) return null;

  const analytics = consent ?? me.user.consentAnalytics;

  async function openPortal() {
    setBusy(true);
    setPortalError(null);
    try {
      const { url } = await api.portal(getToken);
      window.location.href = url;
    } catch {
      setPortalError("Couldn't open the billing portal. Try again in a moment.");
      setBusy(false);
    }
  }

  async function toggleConsent(next: boolean) {
    const previous = analytics;
    setConsent(next);            // optimistic
    setConsentError(null);
    try {
      await api.setConsent(getToken, next);
    } catch {
      setConsent(previous);      // revert, so the box never lies about what was saved
      setConsentError("That didn't save. Your setting is unchanged.");
    }
  }

  return (
    <>
      {/* PHASE 9: back from a one-time unlock's checkout (`/account?unlock=<product>`).
          The unlock lands with the webhook, so the answer the rooms cached is dropped. */}
      {unlockBack && (
        <Notice kind="ok">
          Payment received for {UNLOCKS[unlockBack].label}. It unlocks when Stripe&rsquo;s
          webhook arrives, usually within a few seconds — if the room still shows the lock,
          wait a moment and reload it.
        </Notice>
      )}

      {params.get("checkout") === "success" && (
        <Notice kind="ok">
          Payment received. If the plan below still reads Free, wait a moment and refresh —
          it changes when Stripe's webhook arrives, not when you return from checkout.
        </Notice>
      )}

      <Card>
        <div className="card-head">
          <div>
            <h1>Account</h1>
            <p className="muted small">{me.user.email}</p>
          </div>
          <PlanBadge label={me.plan.label} status={me.plan.status} />
        </div>

        <UsageMeter used={me.usage.used} limit={me.usage.limit} />

        <dl className="kv">
          <dt>{me.plan.status === "canceled" ? "Access ends" : "Renews"}</dt>
          <dd className="num">{when(me.plan.expiresAt)}</dd>
          {me.plan.trialEndsAt !== null && (
            <>
              <dt>Trial ends</dt>
              <dd className="num">{when(me.plan.trialEndsAt)}</dd>
            </>
          )}
          <dt>Plays</dt>
          <dd>
            {me.user.playsWhere ? PLAYS_LABEL[me.user.playsWhere] : "—"}
            {me.user.stakes ? <span className="muted"> · {me.user.stakes}</span> : null}
          </dd>
          <dt>Table sizes</dt>
          <dd className="num">{me.plan.tableSizes.join(", ")}-max</dd>
        </dl>

        {portalError && <Notice kind="err">{portalError}</Notice>}

        <div className="row">
          {me.plan.id === "pro" ? (
            <button className="btn" onClick={openPortal} disabled={busy}>
              {busy ? "Opening…" : "Manage billing"}
            </button>
          ) : (
            <Link className="btn" to="/pricing">
              Start {me.pricing.trialDays}-day trial
            </Link>
          )}
          <button className="btn-quiet" onClick={() => void refresh()} disabled={loading}>
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </Card>

      {/*
        * THE WALLET CARD.
        *
        * The balance, the last twenty movements, and the sentence that is the whole
        * product decision behind Buster Bucks. It sits above Privacy and below the
        * plan because it is the one number on this page that is not about billing, and
        * a player who has come here to find out where their Bucks went should not have
        * to scroll past a subscription to do it.
        *
        * The ledger is shown rather than summarised on purpose. It is append-only, it
        * is the record the balance is derived from, and a play-money balance that
        * cannot be audited by the person holding it is a balance they have to take on
        * trust for no reason.
        */}
      <Card>
        <h2>Buster Bucks</h2>
        <div className="wallet-card-top">
          <span className="wallet-card-bal"><BucksAmount n={wallet.balance} /></span>
          {wallet.canStake && (
            <button className="btn" type="button" onClick={() => setBarkeep(true)}>Ask the Barkeep</button>
          )}
        </div>
        <p className="muted small">
          Buster Bucks are practice money. They refill. There is nothing to buy.
          {" "}They are not redeemable, not transferable, and they fund no prize — whenever your
          balance drops below {wallet.floor.toLocaleString("en-US")}, the Barkeep stakes you back
          to {wallet.stakeTo.toLocaleString("en-US")}, as often as you need it.
        </p>
        {wallet.recent.length > 0 && (
          <table className="wallet-ledger">
            <thead>
              <tr><th>When</th><th>What</th><th className="num">Change</th><th className="num">Balance</th></tr>
            </thead>
            <tbody>
              {wallet.recent.map((e) => (
                <tr key={e.id}>
                  <td className="muted">{when(e.created_at)}</td>
                  <td>{LEDGER_WORDS[e.reason] ?? e.reason}</td>
                  <td className={"num " + (e.delta >= 0 ? "up" : "down")}>
                    {e.delta >= 0 ? "+" : "−"}{Math.abs(e.delta).toLocaleString("en-US")}
                  </td>
                  <td className="num">{e.balance_after.toLocaleString("en-US")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {barkeep && <Barkeep onClose={() => setBarkeep(false)} />}
      </Card>

      <Card>
        <h2>Privacy</h2>
        <label className="check">
          <input type="checkbox" checked={analytics} onChange={(e) => void toggleConsent(e.target.checked)} />
          <span>
            Include my anonymised results in aggregate statistics. Counts only — no hands, no
            identity. Off by default, and you can turn it off again at any time.
          </span>
        </label>
        {consentError && <Notice kind="err">{consentError}</Notice>}
      </Card>

      {/*
        This card said "Not built yet" for as long as the leak report has been live —
        copy that outlived the thing it described, on the one screen a paying customer
        opens to check what they are getting. A promise of a future feature reads very
        differently from a link to a shipped one.
      */}
      <Card>
        <h2>Leak report</h2>
        <p className="muted">
          Where your avoidable loss actually sits, broken down by decision, position,
          stack depth, tournament stage and table size — and what you are already
          doing well.
        </p>
        <p className="muted small">
          {me.plan.capabilities.includes("full_leak_report")
            ? "Your plan includes the full breakdown."
            : "Free accounts see the single most expensive leak. Pro shows every segment."}
        </p>
        <Link className="btn-quiet" to="/report">Open the leak report</Link>
      </Card>

      {/*
        * ===================== PHASE 2B: STATS LIVE HERE NOW ======================
        * Stats left the rooms' tab bar (master plan §4, decision 1): a player who only
        * plays never has to see it, and the one number that matters is in the room
        * header on every screen instead. This is where the rest of it comes to rest.
        *
        * The NUMBERS are not this phase's to invent. Trainer Rating is the trainer's
        * and net Bucks is Phase 4's wallet, and a figure nobody has is the same lie the
        * Phase 2 review took off the felt — so this says where each one comes from and
        * points at the two readings that are real today.
        * =========================================================================
        */}
      <Card>
        <h2>Stats</h2>
        <p className="muted small">
          Your headline number is <b>Trainer Rating</b> — it scores the EV you gave up,
          not whether the hand won — and it appears in the room header once the trainer
          has enough graded spots to mean anything. Net Bucks joins it when the wallet
          does.
        </p>
        <p className="muted small">
          What is already here: the leak report above, and every session's own numbers
          under the table you played it at.
        </p>
      </Card>

      {/*
        * The persona picker, in its second home.
        *
        * The first is the Basics door, which is where a new account meets it. This is
        * where somebody changes their mind. Same component, no gate — Phase F's
        * Founder Ladder adds one here by wrapping it, not by rewriting it.
        */}
      <Card>
        <PersonaPicker />
      </Card>

      {me.plan.id === "free" && (
        <p className="muted small footnote">
          Pro is {money(me.pricing.monthly.amountCents)} a month or{" "}
          {money(me.pricing.annual.amountCents)} a year.
        </p>
      )}
    </>
  );
}
